import type {
  HelpArticle,
  HelpComment,
  PopularTopic,
} from "@/types/help";

// ---------------------------------------------------------------------------
// Mock fixtures for the Help Center. These stand in for a future help/articles
// API — `help.api.ts` returns copies of this data so pages can be wired to real
// endpoints later without changing the UI.
// ---------------------------------------------------------------------------

const ADAM = {
  name: "Adam Smith",
  email: "adam.smith@example.com",
};

export const MOCK_ARTICLES: HelpArticle[] = [
  {
    id: "invite-your-team",
    title: "Invite your team & set roles",
    category: "getting-started",
    excerpt:
      "Add staff, assign roles, and scope org / region / location access so the right people see the right data.",
    body: [
      "Team members are added from User Management (Admin → Users). Create an account with the person's name and email; by default YesChef emails them an invitation and asks them to set their own password the first time they sign in.",
      "Every user has a single role that defines both what they can do and which data they see. Roles run from organization-wide (Concept Admin) down to a single site (Location Manager, Chef, Staff), with Region Manager for multi-location clusters.",
      "A role is tied to a place in the organization → region → location hierarchy, so a Chef at the Downtown location only sees Downtown's recipes, inventory, and team, while a Region Manager sees every location in their region. Pick the narrowest role that still lets someone do their job.",
      "To change a role later, open the person in Admin → Users and choose a new role from the Role dropdown — the change applies right away.",
    ],
    readTimeMinutes: [5, 10],
    author: ADAM,
    publishedAt: "2026-02-22",
    likeCount: 16,
    bookmarkCount: 16,
    commentCount: 5,
  },
  {
    id: "recipe-workflow-basics",
    title: "Recipe workflow basics",
    category: "guides",
    excerpt:
      "How recipes move from draft to service — menu items, prep recipes, and the ingredients that tie them together.",
    body: [
      "YesChef splits recipes into three tabs: Menu Items (finished dishes served to guests), Prep Recipes (components you make ahead, like a sauce or dressing), and Ingredients (the raw and prepared materials those recipes use).",
      "Every recipe starts as a Draft. You build it up by adding ingredients with their quantities and units, writing the execution steps, and — for prep recipes — setting shelf life, storage, and the material it yields.",
      "Prep recipes feed into menu items, and even into other prep recipes, so a dressing you prep in the morning flows straight into the salads you plate during service.",
      'When a recipe is ready, publish it to make it available across your locations — see "Recipe publishing" for the details.',
    ],
    readTimeMinutes: [10, 15],
    author: ADAM,
    publishedAt: "2026-02-22",
    likeCount: 12,
    bookmarkCount: 9,
    commentCount: 3,
  },
  {
    id: "data-looks-outdated",
    title: "Data looks outdated",
    category: "troubleshooting",
    excerpt:
      "Seeing stale numbers right after a change? Here's why it happens and how to get the latest data.",
    body: [
      "To stay fast, YesChef briefly caches data you've already loaded, and reads come from a replica of the database that trails the main copy by a moment. Right after a busy change you may see slightly older numbers for a second or two.",
      "The quickest fix is a hard refresh (Cmd/Ctrl+Shift+R), which re-fetches the latest data from the server. Navigating away and back to a page also triggers a fresh load.",
      'In Working mode, what you see is scoped to the location selected in the top bar. If something looks missing, check that the right location — or "All locations" — is chosen.',
      "If numbers still look wrong after a refresh and a short wait, it's worth reporting. Contact support with what you expected to see and where.",
    ],
    readTimeMinutes: [5, 10],
    author: ADAM,
    publishedAt: "2026-02-22",
    likeCount: 4,
    bookmarkCount: 2,
    commentCount: 1,
  },
  {
    id: "bulk-user-actions",
    title: "New: Bulk user actions",
    category: "whats-new",
    excerpt:
      "Coming soon: select several team members and update their roles or access in a single step.",
    body: [
      "Managing a large team one person at a time is slow. Bulk user actions — rolling out soon — will let you select several team members at once and apply a change in one step.",
      "You'll be able to reassign roles, adjust region or location access, and deactivate departing staff across a whole group, instead of editing each profile individually.",
      "It builds on the bulk tools already in YesChef: in the Recipes section you can turn on selection mode, tick multiple recipes, and publish or export them together.",
      "We'll post here when bulk user actions go live. Until then, you can manage individuals from Admin → Users.",
    ],
    readTimeMinutes: [5, 10],
    author: ADAM,
    publishedAt: "2026-02-20",
    likeCount: 21,
    bookmarkCount: 14,
    commentCount: 2,
  },
  {
    id: "locations-region-setup",
    title: "Locations & region setup",
    category: "getting-started",
    excerpt:
      "Set up the places your team works — group locations into regions and fill in each site's details.",
    body: [
      'YesChef structures everything as Organization → Concept → Region → Location, where a concept is one of the brands you operate and a region groups its locations geographically (for example "West Coast" or "Northeast"). Inventory, menus, and the actual kitchen work all happen at the location level.',
      "During onboarding you add regions first — each has a name and a unique uppercase code like WEST-COAST — then add locations beneath them. You can add several of each and remove any you don't need before moving on.",
      "Each location carries its own details: name and optional code, address, postal code, city/state/country, phone, email, and timezone (defaulting to America/Los_Angeles).",
      "You can revisit all of this later from the Regions and Locations pages, which use the same forms. Assigning a location to a region is optional, but it makes managing multiple sites much easier.",
    ],
    readTimeMinutes: [5, 10],
    author: ADAM,
    publishedAt: "2026-02-18",
    likeCount: 8,
    bookmarkCount: 6,
    commentCount: 0,
  },
  {
    id: "recipe-publishing",
    title: "Recipe publishing",
    category: "guides",
    excerpt:
      "What publishing a recipe actually does, how to do it, and how to take one back to draft.",
    body: [
      "Publishing makes a recipe official and available across the regions and locations it's shared with — it's the step that lets a recipe appear on menus and be used during service. Draft recipes stay in the build process and won't show up in menu generation.",
      "To publish, open a recipe and click Publish in the header. A confirmation explains that it will become visible everywhere it's shared and that you can still edit it afterwards, and the status badge switches from Draft (gray) to Published (blue).",
      "Publishing isn't a lock — published recipes stay fully editable. If you need to pull one back, click Change to Draft and it returns to work-in-progress with all its data intact.",
      "Need to release a whole set at once? Use selection mode in the Recipes list to publish (or unpublish) many recipes together — handy when rolling out a seasonal menu.",
    ],
    readTimeMinutes: [10, 15],
    author: ADAM,
    publishedAt: "2026-02-15",
    likeCount: 11,
    bookmarkCount: 7,
    commentCount: 4,
  },
];

const MOCK_COMMENT_BODY =
  "Lorem ipsum dolor sit amet consectetur. Commodo eu viverra justo dignissim laoreet pulvinar adipiscing amet dui. Morbi viverra facilisis eget eu pharetra arcu sodales phasellus. Sollicitudin dui nunc lectus elementum scelerisque lacinia.";

export const MOCK_COMMENTS: HelpComment[] = Array.from({ length: 5 }, (_, i) => ({
  id: `comment-${i + 1}`,
  author: { name: "Deborah Emmerich", email: "Deborah.Emmerich@email.com" },
  createdAt: "2026-02-15T15:45:00.000Z",
  body: MOCK_COMMENT_BODY,
}));

export const MOCK_POPULAR_TOPICS: PopularTopic[] = [
  {
    id: "user-roles-access",
    title: "User roles & access scope",
    description: "Who can see and edit what — by organization, region, and location.",
    articleId: "invite-your-team",
  },
  {
    id: "locations-region",
    title: "Locations & region setup",
    description: "Create regions and fill in each location's details.",
    articleId: "locations-region-setup",
  },
  {
    id: "recipe-publishing",
    title: "Recipe publishing",
    description: "Take a recipe from draft to live on your menus.",
    articleId: "recipe-publishing",
  },
  {
    id: "troubleshooting-permissions",
    title: "Troubleshooting permissions",
    description:
      'Fix "access denied" and missing menu items by checking your role and scope.',
    articleId: "data-looks-outdated",
  },
];
